import React from "react";
import { pipe } from "fp-ts/function";
import * as A from "fp-ts/Array";
import * as O from "fp-ts/Option";
import * as R from "fp-ts/Record";

import { BarChart } from "src/components/charts/bar_chart";
import { Separator } from "src/components/separator/separator";
import { Body, PageTitle, SectionTitle } from "src/components/typography/typography";
import { constructTitle } from "src/pages/reports/construct_notes";
import { DomainsCircle } from "src/pages/reports/fsci/v2/graphics/domains_circle";
import { FSCIBrandedV2Report } from "src/pages/reports/fsci/v2/types";
import { isNotNil } from "src/utils/type_guards";

import { meta } from "./meta";

/*
 * Types
 */

export type ExpertiseResourcesProps = {
    readonly report: FSCIBrandedV2Report;
};

/*
 * Main
 */

export const ExpertiseResources = (props: ExpertiseResourcesProps): React.ReactElement => {
    const { report } = props;

    return (
        <div className="main-report-content-wrap">
            <div className="single-domain-result-top-wrap">
                <DomainsCircle activeDomain="expertiseAndResources" inline />

                <PageTitle>Expertise & Resources</PageTitle>
                <Body>
                    While a sense of purpose is essential for Christian school leaders and teachers, their ability to
                    fulfill that purpose is necessarily dependent upon their expertise as educators. FCSI research shows
                    that flourishing is connected to excellence in educational and school management practices.
                </Body>
                <Body>
                    Constructs related to expertise &amp; resources were significant for five of the seven participant
                    groups surveyed by the FSCI (parents; teachers; leaders/administrators; support staff; and board
                    members).
                </Body>
                <Body>
                    <em>
                        Construct and benchmark scores are presented as raw scores converted to a 100-point scale (a
                        common industry practice called criterion referencing). These scores give the reader some sense
                        of how strongly respondents agreed with the items within a construct (school score), which is
                        presented alongside the average respondent score in the normative sample (benchmark score).
                        Benchmark data is not included for the purpose of scoring or ranking your school against other
                        schools; rather, it is provided to help your school a) gauge priorities and b) track change over
                        subsequent administrations of the FSCI at your school.
                    </em>
                </Body>
                <Separator />
            </div>
            {pipe(
                R.keys(report.value.results.raw.expertiseAndResources),
                A.map((construct) => (
                    <div className="an-opportunity-item domain-result" key={construct}>
                        {pipe(
                            R.lookup(construct, meta),
                            O.map((m) => (
                                <>
                                    <SectionTitle>{constructTitle(m)}</SectionTitle>
                                    <Body>{m.prologue}</Body>
                                    <BarChart
                                        data={[
                                            {
                                                label: report.value.year,
                                                value: report.value.results.raw.expertiseAndResources[construct] * 100,
                                            },
                                            {
                                                label: "FSCI Benchmark",
                                                value:
                                                    report.value.results.benchmarks.expertiseAndResources[construct] *
                                                    100,
                                            },
                                        ]}
                                    />
                                    {isNotNil(m.extra) && <Body className="small-domain-text">{m.extra}</Body>}
                                </>
                            )),
                            O.getOrElse(() => <></>),
                        )}
                    </div>
                )),
            )}
        </div>
    );
};
